import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BidType, StatisticsResponse } from '../models/api-response.models';
import {
  BidHistoryResponse,
  ChartPoint,
  DashboardResponse,
  SettlementResponse,
} from './utility.service';

export interface PartnerProfileResponse {
  id: string;
  code: string;
  name: string;
  active: boolean;
  timezone: string;
}

/**
 * Partner-scoped dashboard API source.
 *
 * The backend resolves the partner from the authenticated operator token, so
 * these calls never send a partner_id, user_id or utility_id: the scope cannot
 * be widened by request parameters.
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationPartnerService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  private createCompleteUrl(path: string): string {
    return `${this.apiUrl}${path}`;
  }

  getProfile(): Observable<PartnerProfileResponse> {
    return this.http.get<PartnerProfileResponse>(
      this.createCompleteUrl(`integration-partners/me`)
    );
  }

  getDashboard(): Observable<DashboardResponse> {
    return this.http.get<DashboardResponse>(
      this.createCompleteUrl(`integration-partners/me/dashboard`)
    );
  }

  getStatistics(
    start_date: string,
    end_date: string,
    bid_type: BidType = BidType.Preferential
  ): Observable<StatisticsResponse> {
    const body = { start_date, end_date, bid_type };
    return this.http.post<StatisticsResponse>(
      this.createCompleteUrl(`integration-partners/me/statistics`),
      body
    );
  }

  getChart(
    start_date: string,
    end_date: string,
    bid_type: BidType = BidType.Preferential
  ): Observable<ChartPoint[]> {
    const body = { start_date, end_date, bid_type };
    return this.http.post<ChartPoint[]>(
      this.createCompleteUrl(`integration-partners/me/statistics/chart`),
      body
    );
  }

  getBidHistory(
    start_date: string,
    end_date: string,
    limit: number,
    offset: number,
    bid_type?: string
  ): Observable<BidHistoryResponse> {
    let params = new HttpParams()
      .set('start_date', start_date)
      .set('end_date', end_date)
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (bid_type && bid_type.toLowerCase() !== 'all') {
      params = params.set('bid_type', bid_type);
    }

    return this.http.get<BidHistoryResponse>(
      this.createCompleteUrl(`integration-partners/me/statistics/history`),
      { params }
    );
  }

  getSettlementHistory(
    start_date: string,
    end_date: string
  ): Observable<SettlementResponse> {
    const body = { start_date, end_date };
    return this.http.post<SettlementResponse>(
      this.createCompleteUrl(`integration-partners/me/statistics/settlement`),
      body
    );
  }

  getImportExport(offset: number = 0): Observable<any> {
    const params = new HttpParams()
      .set('offset', String(offset))
      .set('_ts', String(Date.now()));
    return this.http.get<any>(
      this.createCompleteUrl(`integration-partners/me/energy/import-export`),
      { params }
    );
  }

  getPaymentTransactions(params: HttpParams): Observable<any> {
    return this.http.get<any>(
      this.createCompleteUrl(`integration-partners/me/payment/transactions`),
      { params }
    );
  }
}
